import { Button, Input, SingleCombobox } from '@commenda/kakashi';
import { TWorkflowDefinition, WorkflowStepType } from '@commenda/types';
import { z } from 'zod';
import { useNotification } from '../../../hooks/useNotification';
import { useForm } from '../../../hooks/useZodForm';
import { useWorkflowTaskTemplate } from '../../context/WorkflowTaskTemplateContext';

const schema = z.object({
  name: z.string().trim().optional(),
  description: z.string().trim().optional(),
  legacyServiceRequestTaskGroupKey: z.string().trim().optional(),
});

type TAddGroupFormFields = z.infer<typeof schema>;

export const AddGroupForm = ({
  onGroupAdd,
  onCancel,
  workflowDefinition,
}: {
  onGroupAdd: (params: {
    name: string;
    description?: string;
    legacyServiceRequestTaskGroupKey?: string;
  }) => void;
  onCancel: () => void;
  workflowDefinition: TWorkflowDefinition;
}) => {
  const { taskGroups: legacyServiceTaskGroups } = useWorkflowTaskTemplate();

  const { setNotification } = useNotification();
  const { register, handleSubmit, watch, setValue } =
    useForm<TAddGroupFormFields>({
      defaultValues: {
        name: '',
        description: '',
      },
    });

  const subWorkflowSteps = workflowDefinition.steps?.filter(
    (step) => step.type === WorkflowStepType.SYS_SUB_WORKFLOW
  );
  const subWorkflowStepsOptions = subWorkflowSteps.map((step) => ({
    label: step.name,
    value: step.id,
  }));

  const legacyServiceTaskGroupsOptions = legacyServiceTaskGroups?.map(
    (taskGroup) => ({
      label: taskGroup.taskGroupId,
      value: taskGroup.taskGroupId,
    })
  );

  const onSubmit = (data: TAddGroupFormFields) => {
    if (!data.name) {
      setNotification({
        title: 'Error',
        message: 'Group Name is required',
        show: true,
        valid: false,
      });
      return;
    }
    onGroupAdd({
      name: data.name,
      description: data.description,
      legacyServiceRequestTaskGroupKey: data.legacyServiceRequestTaskGroupKey,
    });
    onCancel();
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-bg-secondary rounded-md">
      <div className="flex flex-col gap-4">
        <Input placeholder="Group Name" {...register('name')} />
        <SingleCombobox
          options={legacyServiceTaskGroupsOptions}
          placeholder="Select Legacy Service Request Task Group"
          value={watch('legacyServiceRequestTaskGroupKey')}
          setValue={(value) =>
            setValue('legacyServiceRequestTaskGroupKey', value)
          }
        />
      </div>

      <div>
        <Input placeholder="Group Description" {...register('description')} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit(onSubmit)}>
          Add
        </Button>
      </div>
    </div>
  );
};
